import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  useTheme,
  Skeleton,
  Tabs,
  Tab,
  Typography
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';
import { useInventoryByProperty } from '../hooks/useDashboardData';

interface InventoryBreakdownChartProps {
  properties?: string[];
  selectedProperty?: string;
  onPropertyChange?: (property: string) => void;
  data?: any;
  isLoading?: boolean;
  error?: Error | null;
}

const COLORS = [
  '#4F8DFD',
  '#22c55e',
  '#f59e42',
  '#ef4444',
  '#38bdf8',
  '#818cf8',
  '#f472b6',
  '#22d3ee'
];

export const InventoryBreakdownChart: React.FC<
  InventoryBreakdownChartProps
> = ({
  properties = ['brand', 'season', 'category', 'gender'],
  selectedProperty: externalSelectedProperty,
  onPropertyChange,
  data: externalData,
  isLoading: externalIsLoading,
  error: externalError
}) => {
  const theme = useTheme();
  const [internalSelectedProperty, setInternalSelectedProperty] = useState(properties[0]);

  const selectedProperty = externalSelectedProperty !== undefined ? externalSelectedProperty : internalSelectedProperty;

  const {
    data: fetchedData,
    isLoading: fetchedIsLoading,
    error: fetchedError
  } = useInventoryByProperty(selectedProperty);

  const data = externalData !== undefined ? externalData : fetchedData;
  const isLoading = externalIsLoading !== undefined ? externalIsLoading : fetchedIsLoading;
  const error = externalError !== undefined ? externalError : fetchedError;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (onPropertyChange) {
      onPropertyChange(newValue);
    } else {
      setInternalSelectedProperty(newValue);
    }
  };

  const chartData = data?.breakdown && Array.isArray(data.breakdown)
    ? data.breakdown.map((item) => ({
        name: item.value,
        value: item.quantity
      }))
    : [];

  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent
  }: any) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
    const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: '0.875rem', fontWeight: 600 }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: theme.general.borderRadius,
        boxShadow: theme.colors.shadows.card
      }}
    >
      <CardHeader
        title={
          <Typography variant="h4" fontWeight={700}>
            Inventory Breakdown
          </Typography>
        }
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={selectedProperty}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'capitalize',
                fontWeight: 600,
                fontSize: '0.875rem'
              }
            }}
          >
            {properties.map((property) => (
              <Tab key={property} label={property} value={property} />
            ))}
          </Tabs>
        </Box>

        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 300
            }}
          >
            <Skeleton variant="circular" width={300} height={300} />
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 300
            }}
          >
            <Typography color="error">Failed to load data</Typography>
          </Box>
        ) : chartData && chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.general.borderRadiusSm
                }}
              />
              <Legend
                wrapperStyle={{
                  paddingTop: '20px',
                  fontSize: '0.875rem'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 300
            }}
          >
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        )}

        {data && chartData && chartData.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Total Items: {data.breakdown.reduce((sum, item) => sum + item.quantity, 0).toLocaleString()}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};